import { ReactElement, useEffect } from 'react';
import { FormikContextType } from 'formik';
import withComponentLoader from '../../../../hoc/withComponentLoader';
import AppWrappedInput from './AppWrappedInput';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

/**
 * Dropdown (select) input base component stylized to work with Custom App Request Form
 * @param {Object} props component properties
 * @param {Function} props.setLoading setter function coming from `withComponentLoader` HOC
 * @param {string} props.label display text
 * @param {FormikContextType} props.formik full Formik Context
 * @param {string[]} props.options list of options to display.  Should be list of strings.
 * @param {string|ReactElement} props.helperText helper text
 * @param {Function} props.onChange optional `onChange` handler to be called with native `onChange`
 * @param {string} props.noValueOverride optional string to display in place of "None" as the no-value option in the dropdown
 * @param {boolean} props.isRequired flag to determine whether to show required indicator
 * @param {boolean} props.isLoading flag to indicate the loading state (comes from parent)
 * @param {boolean} props.isDisabled flag to indicate whether input is disabled
 * @param {object} props.props the rest of the props
 * @returns Dropdown (select) input stylized for custom
 */
const AppWrappedDropdown = ({
	setLoading,
	label,
	formik,
	options,
	helperText,
	onChange,
	noValueOverride,
	isRequired = false,
	isLoading = false,
	isDisabled = false,
	...props
}) => {
	useEffect(() => {
		setLoading(isLoading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	return (
		<AppWrappedInput
			label={label}
			error={
				formik.touched[props.name] ? formik.errors[props.name] : null
			}
			isRequired={isRequired}
		>
			<FormControl variant='filled' fullWidth disabled={isDisabled}>
				<InputLabel id={'dropdown-label-' + props.name}>
					{'Select ' + label}
				</InputLabel>
				<Select
					labelId={'dropdown-label-' + props.name}
					id={'dropdown-' + props.name}
					name={props.name}
					value={formik.values[props.name]}
					onChange={e => {
						onChange && onChange(e);
						formik.handleChange(e);
					}}
					onBlur={formik.handleBlur}
				>
					<MenuItem value='' key={'dropdown-option--none'}>
						<em>{noValueOverride ?? 'None'}</em>
					</MenuItem>
					{options?.map(option => (
						<MenuItem
							value={option}
							key={'dropdown-option--' + option}
						>
							{option}
						</MenuItem>
					))}
				</Select>
				<FormHelperText>{helperText}</FormHelperText>
			</FormControl>
		</AppWrappedInput>
	);
};
export default withComponentLoader(AppWrappedDropdown);
